import { useTranslation } from 'react-i18next'

import type { TGetSimulacoesRelacionadas } from '@/features/simulacao/interfaces/simulacao.type'
import { CardSelecaoSimulacaoWrapper } from '@/pages/proposta-listar-produtos/CardSelecaoSimilacao/card-selecao-simulacao-wrapper'
import { Checkbox } from '@/shared/components/ui'
import { Badge } from '@/shared/components/ui/badge'
import { useIsMobile } from '@/shared/hooks'
import { formatToBRL } from '@/shared/utils/currency.utils'
import { formatDaysSince } from '@/shared/utils/date.utils'

export type TCardSelecaoSimulacaoProps = {
  simulacao: TGetSimulacoesRelacionadas['relacionadas'][number]
  selected: boolean
  onToggleSelect: (simulacao: string) => void
}

export default function CardSelecaoSimulacao({
  simulacao,
  selected,
  onToggleSelect,
}: Readonly<TCardSelecaoSimulacaoProps>) {
  const { t } = useTranslation('proposta')
  const isMobile = useIsMobile()

  const daysAgo = formatDaysSince(simulacao.quantidadeDias)
  const produtoIndisponivel = simulacao.produto.status !== 'Disponivel'

  const simulationStatus = {
    text:
      simulacao.status === 'Enviada' ? t('selecao_produto.simulacao.enviada') : t('selecao_produto.simulacao.gerada'),
    variant: 'warning' as const,
  }

  const produtoStatus = {
    text: produtoIndisponivel ? t('selecao_produto.produto.indisponivel') : t('selecao_produto.produto.disponivel'),
    variant: produtoIndisponivel ? ('default' as const) : ('success' as const),
  }
  const towerInfoType =
    simulacao.tipoDoProduto === `Acessorio` ? t('selecao_produto.torre_acessorio') : t('selecao_produto.torre_unidade')
  const isSelectable = !produtoIndisponivel
  const handleToggleProduct = () => {
    if (isSelectable) {
      onToggleSelect(simulacao.id)
    }
  }

  return (
    <CardSelecaoSimulacaoWrapper
      isMobile={isMobile}
      isSelectable={isSelectable}
      selected={selected}
      onToggle={handleToggleProduct}
    >
      <div className="font-nunito grid grid-cols-[32px_1fr]" data-testid="card-simulacao">
        <div className="flex items-center justify-center border-neutral-400/20 px-4 py-6">
          {isSelectable && (
            <div className="relative">
              <Checkbox
                id={`simulacao-${simulacao.id}`}
                checked={selected}
                onCheckedChange={isMobile ? undefined : handleToggleProduct}
                className="ml-3 rounded-sm data-[state=checked]:text-white"
              />
              {!isMobile && (
                <label
                  htmlFor={`simulacao-${simulacao.id}`}
                  className="absolute inset-0 -mt-3 -ml-[2px] h-12 w-12 cursor-pointer"
                >
                  <span className="sr-only">card-simulacao</span>
                </label>
              )}
            </div>
          )}
        </div>
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between" data-testid="card-simulacao-header">
            <div className="flex flex-wrap items-center gap-2 text-neutral-600">
              <span className="font-semibold">{simulacao.diaMes}</span>
              <div data-ismobile={isMobile} className="h-4 w-px bg-gray-300 data-[ismobile=true]:hidden" />
              <span className="text-sm"> {daysAgo}</span>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              <Badge variant={simulationStatus.variant} className="py-1 text-sm shadow-none">
                {simulationStatus.text}
              </Badge>
              <Badge variant={produtoStatus.variant} className="py-1 text-sm shadow-none">
                {produtoStatus.text}
              </Badge>
            </div>
          </div>
          <div className="border-t border-b border-neutral-100 text-sm">
            <div
              className="grid grid-cols-1 border-b border-neutral-100 border-neutral-400/20 py-2 md:grid-cols-2 md:gap-4 md:border-b-0"
              data-testid="card-simulacao-empreendimento"
            >
              <div className="border-b pb-2 md:border-r md:border-b-0 md:pb-0">
                <p className="mb-1 font-semibold text-neutral-600">{t('selecao_produto.empreendimento')}</p>
                <p className="text-neutral-700">{simulacao.nomeEmpreendimento}</p>
              </div>
              <div className="pt-2 md:pt-0">
                <p className="mb-1 font-semibold text-neutral-600">{towerInfoType}</p>
                <p className="text-neutral-700">
                  {simulacao.nomeBloco} - {simulacao.produto.codigo}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-1 text-sm" data-testid="card-simulacao-valor">
            <p className="mb-1 font-semibold text-neutral-600">{t('selecao_produto.valor_negociado')}</p>
            <p className="text-neutral-700">{formatToBRL(simulacao.valorTotalSimulado)}</p>
          </div>
        </div>
      </div>
    </CardSelecaoSimulacaoWrapper>
  )
}
